import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import authService from './services/authService';
import { NAV_MENU_ITEMS } from './config/constants';

// Import all main pages
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ProductManagement from './pages/ProductManagement';
import ProductQRManagement from './pages/ProductQRManagement';
import Sales from './pages/Sales';
import UserManagement from './pages/UserManagement';
import Suppliers from './pages/Suppliers';
import Customers from './pages/Customers';
import Setting from './pages/Setting';
import Reports from './pages/Reports';
import EmployerManagement from './pages/submenus/EmployerManagement/EmployerManagement.js';
import UserLog from './pages/UserLog.js';
import WebOrders from './pages/submenus/OrderManagement/WebOrders.jsx';
import AllOrders from './pages/submenus/OrderManagement/CombinedOrders.jsx';

// Import submenu pages
import AllProducts from './pages/submenus/Products/Products.js';
import Categories from './pages/submenus/Category/Categories.js';
import SubCategories from './pages/submenus/Sub-Category/SubCategories.js';
import GenerateQR from './pages/submenus/ProductQRManagement/ProductQRManagement.js';
import Orders from './pages/submenus/OrderManagement/Orders.js';
import AllUsers from './pages/submenus/User/AllUsers.js';
import PurchaseOrders from './pages/submenus/Suppliers/PurchaseOrders.js';
import ReportsDashboard from './pages/submenus/reports/ReportsDashboard.js';
import SalesReports from './pages/submenus/reports/SalesReports.js';
import InventoryReports from './pages/submenus/reports/InventoryReports.js';
import FinancialReports from './pages/submenus/reports/FinancialReports.js';
import SupplierPendingPaymentsReport from './pages/submenus/reports/SupplierPendingPaymentsReport.js';
import SupplierPendingDetails from './pages/submenus/reports/SupplierPendingDetails.js';
import CustomerReports from './pages/submenus/reports/CustomerReports.js';
import EcommerceReports from './pages/submenus/reports/EcommerceReports.js';
import ProductReports from './pages/submenus/reports/ProductReports.js';
import SupplierPayments from './pages/submenus/Suppliers/SupplierPayments.js';
import SupplierOutstandingPayments from './pages/submenus/Suppliers/SupplierOutstandingPayments.js';

import './App.css';

// Page components mapping
const pageComponents = {
  // Main pages
  'super-admin-dashboard': SuperAdminDashboard,
  'admin-dashboard': AdminDashboard,
  'product-management': ProductManagement,
  'product-qr-management': ProductQRManagement,
  'sales': Sales,
  'user-management': UserManagement,
  'suppliers': Suppliers,
  'customers': Customers,
  'setting': Setting,
  'reports': Reports,
  'employer-management': EmployerManagement,
  'user-log': UserLog,

  // Submenu pages
  'all-products': AllProducts,
  'categories': Categories,
  'sub-categories': SubCategories,
  'generate-qr': GenerateQR,
  'orders': Orders,
  'WebOrders': WebOrders,
  'AllOrders': AllOrders,
  'all-users': AllUsers,
  'purchase-orders': PurchaseOrders,
  'supplier-payments': SupplierPayments,
  'supplier-outstanding-payments': SupplierOutstandingPayments,

  // Report pages
  'all-reports': ReportsDashboard,
  'sales-reports': SalesReports,
  'inventory-reports': InventoryReports,
  'financial-reports': FinancialReports,
  'supplier-Pending-payments-reports': SupplierPendingPaymentsReport,
  'supplier-Pendingdetails-reports': SupplierPendingDetails,
  'customer-reports': CustomerReports,
  'ecommerce-reports': EcommerceReports,
  'product-reports': ProductReports,
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState('');
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('appTheme') || 'light';
  });

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('appTheme', newTheme);
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    let intervalId;

    if (isAuthenticated) {
      // Update time immediately
      updateSriLankaTime();

      // Update time every second
      intervalId = setInterval(updateSriLankaTime, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAuthenticated]);

  const updateSriLankaTime = () => {
    const now = new Date();

    // Format for Sri Lanka time (Asia/Colombo timezone)
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Colombo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    const parts = formatter.formatToParts(now);

    let year, month, day, hour, minute, second, dayPeriod;

    parts.forEach(part => {
      switch (part.type) {
        case 'year': year = part.value; break;
        case 'month': month = part.value; break;
        case 'day': day = part.value; break;
        case 'hour': hour = part.value; break;
        case 'minute': minute = part.value; break;
        case 'second': second = part.value; break;
        case 'dayPeriod': dayPeriod = part.value; break;
        default: break;
      }
    });

    // Format: YYYY-MM-DD HH:MM:SS AM/PM
    const formattedDate = `${year}-${month}-${day}`;
    const formattedTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')} ${dayPeriod}`;

    setCurrentTime(`${formattedDate} ${formattedTime}`);
  };

  const checkAuthentication = async () => {
    try {
      const token = authService.getToken();
      const currentUser = authService.getCurrentUser();

      if (token && currentUser) {
        // Verify token is still valid
        const isValid = await authService.verifyToken();
        if (isValid) {
          setIsAuthenticated(true);
          setUser(currentUser);
          setDefaultPage();
        } else {
          authService.logout();
        }
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      authService.logout();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultPage = () => {
    const permissions = authService.getPermissions();

    if (!permissions || permissions.length === 0) {
      setActivePage('super-admin-dashboard');
      return;
    }

    // Check for super admin permission
    if (permissions.includes(1)) {
      setActivePage('super-admin-dashboard');
      return;
    }

    // Check for admin permission
    if (permissions.includes(2)) {
      setActivePage('admin-dashboard');
      return;
    }

    // Find first accessible page based on permissions
    const accessibleItems = NAV_MENU_ITEMS.filter(item =>
      permissions.includes(item.permission)
    );

    if (accessibleItems.length > 0) {
      const firstItem = accessibleItems[0];
      if (firstItem.submenus && firstItem.submenus.length > 0) {
        setActivePage(firstItem.submenus[0].slug);
      } else {
        setActivePage(firstItem.slug);
      }
    } else {
      // Fallback to super admin dashboard if no permissions match
      setActivePage('super-admin-dashboard');
    }
  };

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    setDefaultPage();
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setActivePage('');
  };

  const handlePageChange = (pageSlug) => {
    console.log('Changing page to:', pageSlug);
    setActivePage(pageSlug);
  };

  const renderPage = () => {
    const PageComponent = pageComponents[activePage];

    if (!PageComponent) {
      console.warn(`No component found for page: ${activePage}`);
      return <div>Page not found: {activePage}</div>;
    }

    return React.createElement(PageComponent, {
      onPageChange: handlePageChange,
      user: user
    });
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading System...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className={`App ${theme === 'dark' ? 'dark-mode' : ''}`}>
      <Sidebar
        activePage={activePage}
        onPageChange={handlePageChange}
        user={user}
      />
      <div className="main-content">
        <header className="main-header">
          <div className="header-left">
            <div className="sri-lanka-clock">
              <div className="clock-icon">🕐</div>
              <div className="clock-content">
                <div className="clock-label">Sri Lanka Time</div>
                <div className="clock-time">{currentTime}</div>
              </div>
            </div>
          </div>

          <div className="header-center">
            <div className="welcome-message">
              Welcome back, <span className="user-name">{user?.name || 'User'}</span>!
            </div>
          </div>

          <div className="header-right">
            <button onClick={toggleTheme} className="theme-toggle-btn" title="Toggle Theme">
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
            <div className="user-role-badge">
              <span className="role-icon">👤</span>
              <span className="role-text">
                {user?.role === 'super_admin' ? 'Super Admin' :
                  user?.role === 'admin' ? 'Administrator' :
                    user?.role || 'User'}
              </span>
            </div>
            <button onClick={handleLogout} className="logout-button">
              <span className="logout-icon">🚪</span>
              Logout
            </button>
          </div>
        </header>

        <div className="page-wrapper">
          {renderPage()}
        </div>
      </div>
    </div>
  );
}

export default App;